// Phase I3 — Adversarial Review.
// Per-leg challenge + response panel; collapsible.
// Faithful port of prototype/super-acca-v2.jsx::AdversarialReview.

import { useState } from "react";
import { summarizeReasoning } from "../../../agents/reasoning.ts";
import { Badge, Eyebrow } from "../../../components/super-acca";
import type { SuperAccaSlip } from "../../../super-acca/view-models";

export interface AdversarialReviewProps {
  slip: SuperAccaSlip;
}

export function AdversarialReview({ slip }: AdversarialReviewProps) {
  const [openId, setOpenId] = useState<number | null>(null);
  const adv = slip.adversarial;
  const total = slip.legs.length;
  const cleared = adv.filter((a) => a.cleared).length;
  const flagged = adv.filter((a) => !a.cleared).length;

  if (adv.length === 0) {
    return (
      <div
        style={{
          marginTop: 28,
          border: "1px solid var(--sa-hairline)",
          background: "var(--sa-bg-panel)",
          borderRadius: 8,
          padding: 24,
        }}
      >
        <Eyebrow>Adversarial Review · Walters Standard</Eyebrow>
        <p
          style={{
            marginTop: 10,
            fontFamily: "var(--sa-font-body)",
            fontSize: 13.5,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.6,
          }}
        >
          No challenge log on file for this slip — adversarial outputs will
          render once the agent debate finalises per-leg challenge/response
          pairs.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 28,
        border: "1px solid var(--sa-hairline)",
        background: "var(--sa-bg-panel)",
        borderRadius: 8,
      }}
    >
      <div
        style={{
          padding: "20px 24px",
          borderBottom: "1px solid var(--sa-hairline)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <Eyebrow>Adversarial Review · Walters Standard</Eyebrow>
          <div
            style={{
              fontFamily: "var(--sa-font-display)",
              fontSize: 22,
              fontWeight: 600,
              marginTop: 8,
              color: "var(--sa-fg-primary)",
            }}
          >
            {cleared} of {total} legs cleared · {flagged} flagged
          </div>
        </div>
        <span
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10.5,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.16em",
          }}
        >
          NEVER PUBLISH A LEG YOU CAN'T ARGUE AGAINST
        </span>
      </div>
      <div>
        {adv.map((entry) => {
          const isOpen = openId === entry.leg_id;
          const leg = slip.legs.find((l) => l.id === entry.leg_id);
          const challenge = summarizeReasoning(entry.challenge);
          const response = summarizeReasoning(entry.response);
          return (
            <div
              key={entry.leg_id}
              style={{ borderBottom: "1px solid var(--sa-hairline)" }}
            >
              <div
                onClick={() => setOpenId(isOpen ? null : entry.leg_id)}
                style={{
                  padding: "14px 24px",
                  display: "grid",
                  gridTemplateColumns: "50px 1fr 120px 24px",
                  gap: 14,
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--sa-font-mono)",
                    fontSize: 11,
                    color: "var(--sa-fg-muted)",
                    letterSpacing: "0.16em",
                  }}
                >
                  L{String(entry.leg_id).padStart(2, "0")}
                </span>
                <div>
                  <div
                    style={{
                      fontFamily: "var(--sa-font-body)",
                      fontSize: 13.5,
                      color: "var(--sa-fg-primary)",
                    }}
                  >
                    {leg?.market ?? "—"} ·{" "}
                    <span style={{ color: "var(--sa-accent-saffron)" }}>
                      {leg?.selection ?? "—"}
                    </span>
                  </div>
                </div>
                {entry.cleared ? (
                  <Badge variant="success">CLEARED</Badge>
                ) : (
                  <Badge variant="warn">⚠ FLAGGED</Badge>
                )}
                <span style={{ color: "var(--sa-fg-muted)", textAlign: "right" }}>
                  {isOpen ? "−" : "+"}
                </span>
              </div>
              {isOpen ? (
                <div
                  style={{
                    padding: "0 24px 18px 86px",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      padding: 14,
                      background: "var(--sa-signal-red-dim)",
                      border: "1px solid rgba(191,93,93,0.2)",
                      borderRadius: 4,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--sa-font-mono)",
                        fontSize: 10,
                        color: "var(--sa-signal-red)",
                        letterSpacing: "0.16em",
                      }}
                    >
                      ⚔ ADVERSARIAL CHALLENGE
                    </div>
                    <p
                      style={{
                        fontFamily: "var(--sa-font-body)",
                        fontSize: 13,
                        color: "var(--sa-fg-secondary)",
                        marginTop: 8,
                        lineHeight: 1.6,
                      }}
                    >
                      {challenge ?? entry.challenge}
                    </p>
                  </div>
                  <div
                    style={{
                      padding: 14,
                      background: "var(--sa-signal-green-dim)",
                      border: "1px solid rgba(93,191,138,0.2)",
                      borderRadius: 4,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--sa-font-mono)",
                        fontSize: 10,
                        color: "var(--sa-signal-green)",
                        letterSpacing: "0.16em",
                      }}
                    >
                      ✓ RESPONSE · LEG {entry.cleared ? "CONFIRMED" : "FLAGGED"}
                    </div>
                    <p
                      style={{
                        fontFamily: "var(--sa-font-body)",
                        fontSize: 13,
                        color: "var(--sa-fg-secondary)",
                        marginTop: 8,
                        lineHeight: 1.6,
                      }}
                    >
                      {response ?? entry.response}
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
